/**
 * cart-affiliate-finder.js - the cart's "Shopping with a Glowming affiliate?" box.
 *
 * Markup and the three states are documented in snippets/cart-affiliate-finder.liquid.
 * The Business App side is GET /api/affiliate/finder (BA PR #1074):
 *   200 { status: 'ok', results: [{ label: 'Mollie V', refCode: 'AV65WK3L' }] }
 *   400 / 403 / 429 / 503 with { status }. 429 and 503 mean "try again", never
 *   "no one found".
 *
 * WHAT A PICK WRITES, and why each:
 *   1. cart attribute `gs_ref` = refCode (POST /cart/update.js). This is the one
 *      signal that reaches the order (note_attributes), and it is exactly what
 *      an affiliate link writes, so M05 credits it the same way.
 *   2. localStorage `glowming_affiliate_ref` {ref, source: 'cart_finder', ts} and
 *      the cookie of the same name, in the Glowming Affiliate Tracker's own
 *      format. The tracker re-affirms `gs_ref` from these on every page and
 *      after every cart change; without them it would know no ref and a later
 *      page could not keep the pick on the cart.
 *
 * PERFORMANCE (operator 2026-09-25, "make sure the cart works without lag"):
 * no request on load unless a ref is already known (then one cached host
 * lookup); searches are debounced, need 2 characters, and a newer search
 * aborts the older one.
 *
 * Pure helpers are exported on window.GlowmingCartFinder for
 * tools/lib/cart-affiliate-finder.test.mjs.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.GlowmingCartFinder) return;

  /** The Affiliate Tracker's storage key and cookie name (affiliate-tracker.js). */
  var STORAGE_KEY = 'glowming_affiliate_ref';
  /** The tracker's 90-day window, so a pick lives as long as a link click. */
  var TTL_DAYS = 90;
  /** The exact ref families the tracker (REF_REGEX) and the Business App
      (CLICK_REF_REGEX) accept: legacy GAI-/GAB-/GCM- codes and the 8-10
      character Glowming refs. Kept identical, so nothing this box accepts is
      refused downstream. */
  var REF_REGEX = /^(?:GA[IB]-\d{5,6}|GCM-\d{3,5}|[a-zA-Z0-9]{8,10})$/;
  var MIN_QUERY = 2;
  var MAX_QUERY = 64;
  var DEBOUNCE_MS = 250;

  /**
   * The trimmed query to send, or null when it must not be sent.
   * @param {string} raw
   * @returns {string|null}
   */
  function queryToSend(raw) {
    var q = String(raw == null ? '' : raw).trim();
    if (q.length < MIN_QUERY || q.length > MAX_QUERY) return null;
    return q;
  }

  /**
   * A ref, or null. Rejects anything that is not a plausible ref, so a tampered
   * cookie or attribute never reaches the cart or a request.
   * @param {*} value
   * @returns {string|null}
   */
  function cleanRef(value) {
    var ref = typeof value === 'string' ? value.trim() : '';
    return REF_REGEX.test(ref) ? ref : null;
  }

  /** The ref in the page address (?gs_ref=), or null (same rule as home-host-card.js). */
  function refFromSearch(search) {
    var m = String(search || '').match(/[?&]gs_ref=([^&#]*)/);
    if (!m) return null;
    try { return cleanRef(decodeURIComponent(m[1].replace(/\+/g, ' '))); } catch (e) { return null; }
  }

  /**
   * The ref this visit already carries, in the tracker's own priority: its
   * stored ref, then its cookie, then the cart attribute the page rendered.
   * The stored entry is `{ref, source, ts}` JSON and EXPIRES after the
   * tracker's 90 days, as the tracker's own readStoredRef treats it; the cookie
   * is the RAW ref, URI-encoded (the tracker's setCookie(COOKIE_NAME, ref)).
   * @param {string|null} storedJson - localStorage value.
   * @param {string} cookieHeader - document.cookie.
   * @param {string} cartRef - cart.attributes.gs_ref as rendered.
   * @param {number} [now] - epoch ms, for tests; defaults to Date.now().
   * @returns {string|null}
   */
  function knownRef(storedJson, cookieHeader, cartRef, now) {
    if (storedJson) {
      try {
        var parsed = JSON.parse(storedJson);
        var at = (typeof now === 'number' ? now : Date.now());
        var fresh = parsed && typeof parsed.ts === 'number' && at - parsed.ts <= TTL_DAYS * 86400000;
        var fromStore = fresh ? cleanRef(parsed.ref) : null;
        if (fromStore) return fromStore;
      } catch (e) { /* a corrupt entry is no entry */ }
    }
    var match = String(cookieHeader || '').match(/(?:^|;\s*)glowming_affiliate_ref=([^;]*)/);
    if (match) {
      var fromCookie = null;
      try { fromCookie = cleanRef(decodeURIComponent(match[1])); } catch (e) { fromCookie = null; }
      if (fromCookie) return fromCookie;
    }
    return cleanRef(cartRef);
  }

  /**
   * "Mollie V" -> "Mollie" for the credit line; a one-word label is kept whole.
   * @param {string} label
   * @returns {string}
   */
  function firstNameOf(label) {
    return String(label || '').replace(/ \S$/, '');
  }

  /**
   * THE PUBLIC NAME SHAPE, enforced here too (Codex theme r1, Medium): the
   * Business App already reduces a surname to one initial, but the box must not
   * print more than "First L" even if an answer ever carried more. A label is
   * accepted only as a first name (letters, spaces, apostrophes, hyphens; up to
   * 40) followed by ONE single-letter initial, or as that first name alone when
   * the answer says it IS the first name (no surname on record). "Mollie
   * Vermaak", an email or anything with digits is refused.
   * KNOWN LIMIT: a multi-word first name ("Anne Marie V") is allowed, so a
   * surname word typed INTO the first-name field upstream cannot be told apart
   * here; the Business App's masking is the primary control.
   */
  var FIRST_NAME = /^[A-Za-zÀ-ɏ][A-Za-zÀ-ɏ' -]{0,39}$/;
  var FIRST_AND_INITIAL = /^[A-Za-zÀ-ɏ][A-Za-zÀ-ɏ' -]{0,39} [A-Za-zÀ-ɏ]$/;

  /**
   * A label safe to print, or null.
   * @param {string} label - "Mollie V" (a trailing period is dropped first).
   * @param {string|null} [firstName] - when given and equal to the label, a bare first name is allowed.
   * @returns {string|null}
   */
  function publicLabel(label, firstName) {
    var l = String(label == null ? '' : label).trim().replace(/\.$/, '');
    if (FIRST_AND_INITIAL.test(l)) return l;
    if (firstName != null && l === String(firstName).trim() && FIRST_NAME.test(l)) return l;
    return null;
  }

  /**
   * The results a 200 body may carry, validated: only {label, refCode} with a
   * label of the public shape and a plausible ref, at most 8. Anything else is
   * dropped. A result label must carry its initial: the finder only ever
   * returns affiliates with a surname-initial label or a bare first name, and a
   * bare name there cannot be told from a full one, so it is dropped rather
   * than risk printing a surname.
   * @param {*} body
   * @returns {{label: string, refCode: string}[]}
   */
  function resultsFrom(body) {
    if (!body || body.status !== 'ok' || !Array.isArray(body.results)) return [];
    var out = [];
    body.results.forEach(function (r) {
      if (out.length >= 8 || !r) return;
      var label = publicLabel(r.label, null);
      var ref = cleanRef(r.refCode);
      if (label && ref) out.push({ label: label, refCode: ref });
    });
    return out;
  }

  /**
   * The host lookup's name as this box shows it: "Thandi M." -> "Thandi M".
   * @param {*} body - the /api/affiliate/host 200 body.
   * @returns {{label: string, first: string}|null}
   */
  function hostName(body) {
    var host = body && body.status === 'ok' ? body.host : null;
    if (!host || typeof host.displayName !== 'string') return null;
    var firstName = typeof host.firstName === 'string' ? host.firstName.trim() : null;
    var label = publicLabel(host.displayName, firstName);
    if (!label) return null;
    /* The credit line's name is the label's own first-name part, never a
       separately supplied string that was not checked against the shape. */
    return { label: label, first: firstNameOf(label) };
  }

  /**
   * A theme-editor label (cart section settings, finder_*) with {name}
   * replaced; the built-in wording when the setting is blank (operator
   * 2026-09-25: the cards' wording is editable in Shopify or the BA).
   */
  function fill(template, name, fallback) {
    var t = typeof template === 'string' && template.trim() ? template.trim() : fallback;
    return t.split('{name}').join(name == null ? '' : name);
  }

  /** Persist a pick where the Affiliate Tracker keeps a link's ref. */
  function persist(ref) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ref: ref, source: 'cart_finder', ts: Date.now() }));
    } catch (e) { /* storage off: the cookie below still carries it */ }
    document.cookie = STORAGE_KEY + '=' + encodeURIComponent(ref) + '; Max-Age=' + (TTL_DAYS * 86400) + '; Path=/; SameSite=Lax' +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  }

  /** Wire one box. */
  function bind(box) {
    var input = box.querySelector('[data-finder-input]');
    var list = box.querySelector('[data-finder-results]');
    var status = box.querySelector('[data-finder-status]');
    var search = box.querySelector('[data-finder-search]');
    var chosen = box.querySelector('[data-finder-chosen]');
    var chosenLine = box.querySelector('[data-finder-chosen-line]');
    var creditLine = box.querySelector('[data-finder-credit-line]');
    var d = box.dataset;
    var changeBtn = box.querySelector('[data-finder-change]');
    if (!input || !list || !search || !chosen) return;

    var timer = null;
    var inFlight = null;
    /** The ref the Affiliate Tracker loaded with on this page, or null. */
    var trackedAtLoad = null;

    function setStatus(text) { if (status) status.textContent = text || ''; }

    function showSearch() {
      box.setAttribute('data-state', 'search');
      chosen.hidden = true;
      search.hidden = false;
    }

    function showChosen(label, first, ref) {
      /* Recorded for the homepage Welcome Block, which fetches her socials. */
      box.setAttribute('data-chosen-ref', ref || '');
      if (chosenLine) chosenLine.textContent = fill(d.labelChosen, label, 'Shopping with {name}');
      if (creditLine) creditLine.textContent = fill(d.labelCredit, first, '{name} gets the credit for this order.');
      /* Recorded for the homepage Welcome Block, which draws its own chosen card. */
      box.setAttribute('data-chosen-label', label || '');
      box.setAttribute('data-state', 'chosen');
      search.hidden = true;
      chosen.hidden = false;
      list.hidden = true;
      list.textContent = '';
      setStatus('');
    }

    function render(results) {
      list.textContent = '';
      results.forEach(function (r) {
        var li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-finder__result';
        btn.setAttribute('role', 'option');
        var name = document.createElement('span');
        name.className = 'cart-finder__result-name';
        name.textContent = r.label;
        var role = document.createElement('span');
        role.className = 'cart-finder__result-role';
        role.textContent = fill(d.labelRole, null, 'Glowming affiliate');
        name.appendChild(role);
        var pick = document.createElement('span');
        pick.className = 'cart-finder__result-pick';
        pick.textContent = fill(d.labelSelect, null, 'Select');
        btn.appendChild(name);
        btn.appendChild(pick);
        btn.addEventListener('click', function () { choose(r); });
        li.appendChild(btn);
        list.appendChild(li);
      });
      list.hidden = results.length === 0;
    }

    function runSearch(q) {
      if (inFlight) inFlight.abort();
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      inFlight = controller;
      fetch(box.dataset.endpoint + '?q=' + encodeURIComponent(q), {
        mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) {
          return res.json().catch(function () { return null; }).then(function (body) { return { ok: res.ok, body: body }; });
        })
        .then(function (answer) {
          if (queryToSend(input.value) !== q) return; /* a stale answer */
          if (!answer.ok) { render([]); setStatus(fill(d.labelBusy, null, 'Search is busy. Please try again in a moment.')); return; }
          var results = resultsFrom(answer.body);
          render(results);
          setStatus(results.length ? '' : fill(d.labelNone, null, 'No affiliate found. Check the spelling or ask them for their code.'));
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          render([]);
          setStatus(fill(d.labelBusy, null, 'Search is busy. Please try again in a moment.'));
        });
    }

    function choose(result) {
      setStatus(fill(d.labelSaving, null, 'Saving...'));
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { gs_ref: result.refCode } })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart ' + res.status);
          persist(result.refCode);
          /* The Affiliate Tracker holds the ref it read at page load in memory
             and re-writes it after EVERY cart change and again at checkout. If
             that ref is someone else's (the shopper came through A's link and
             changed to B), it would put A back on this page view. One reload
             makes the tracker read the new stored ref. A first pick with no
             tracked ref needs none: the tracker never writes an empty ref. */
          if (trackedAtLoad && trackedAtLoad !== result.refCode) {
            window.location.reload();
            return;
          }
          showChosen(result.label, firstNameOf(result.label), result.refCode);
        })
        .catch(function () { setStatus(fill(d.labelSaveFailed, null, 'We could not save that. Please try again.')); });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = queryToSend(input.value);
      if (!q) { if (inFlight) inFlight.abort(); render([]); setStatus(''); return; }
      timer = setTimeout(function () { runSearch(q); }, DEBOUNCE_MS);
    });

    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        showSearch();
        input.value = '';
        input.focus();
      });
    }

    /* A ref already known this visit: show who, from one cached host lookup.
       With no name (unknown or inactive host, or the lookup failed), fall back
       to search rather than show "Shopping with" and a blank. */
    var stored = null;
    try { stored = window.localStorage.getItem(STORAGE_KEY); } catch (e) { stored = null; }
    /* What the tracker itself knows (storage or cookie, not the cart attribute),
       read before any pick: see choose(). */
    trackedAtLoad = knownRef(stored, document.cookie, '');
    /* The page address (?gs_ref=) is the last fallback, for the FIRST page of a
       host-link visit, before the tracker has stored the ref (live bug
       2026-09-28). It shows her as chosen; it does NOT change trackedAtLoad. */
    var ref = knownRef(stored, document.cookie, box.dataset.cartRef) || refFromSearch(window.location.search);
    if (!ref) { showSearch(); return; }
    fetch(box.dataset.hostEndpoint + '?ref=' + encodeURIComponent(ref) + '&source=gs_ref', {
      mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' }
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (body) {
        var name = hostName(body);
        if (name) showChosen(name.label, name.first, ref); else showSearch();
      })
      .catch(showSearch);
  }

  window.GlowmingCartFinder = {
    queryToSend: queryToSend,
    cleanRef: cleanRef,
    knownRef: knownRef,
    refFromSearch: refFromSearch,
    firstNameOf: firstNameOf,
    publicLabel: publicLabel,
    fill: fill,
    resultsFrom: resultsFrom,
    hostName: hostName,
    bind: bind
  };

  if (typeof document !== 'undefined' && document.querySelectorAll) {
    var start = function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-cart-finder]'), function (box) {
        if (box.dataset.bound === '1') return;
        box.dataset.bound = '1';
        bind(box);
      });
    };
    window.GlowmingCartFinder.start = start;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
    /* The cart section is redrawn in place after each change; the new box is a
       new node, bound here. Its ref read is local, and the host lookup is
       browser-cached for 5 minutes, so a redraw costs no extra wait. */
    document.addEventListener('glowming:cart:rendered', start);
  }
})();
